import {
  toWSDateString,
  useWSModalCustom,
  WSAlert,
  WSList,
  WSLoader,
  WSModal,
  WSModalCustomProps,
  WSTable,
  WSTableCell,
  WSTableColumn,
  WSText
} from "@wingspanhq/fe-component-library";
import { IRequirementDefinitionResponse } from "@wingspanhq/payments/dist/interfaces";
import {
  IVersionHistoryItem,
  RequirementStatus
} from "@wingspanhq/payments/dist/interfaces/requirement";
import React from "react";

import { useQueryPayeeRowsSummary } from "../../../../query/search/payee/queries/useQueryPayeeRowsSummary";
import { PayeeRowsQuery } from "../../../../services/search";

type VersionHistoryRow = IVersionHistoryItem & {
  isLatestVersion: boolean;
  requirementDefinitionId: string;
};

type Props = {
  onViewDetails?: () => void;
  requirementDefinition: IRequirementDefinitionResponse;
} & WSModalCustomProps<void>;

const ContractorsCell: React.FC<{
  isLatestVersion: boolean;
  requirementDefinitionId: string;
  version: number;
}> = ({ isLatestVersion, requirementDefinitionId, version }) => {
  const baseFilter: PayeeRowsQuery["filter"] = {
    "requirements.searchableRequirementDefinitionVersion": `${requirementDefinitionId}_${version}`
  };

  const queryTotal = useQueryPayeeRowsSummary({ filter: baseFilter });

  const queryIncomplete = useQueryPayeeRowsSummary({
    filter: {
      ...baseFilter,
      "requirements.status": RequirementStatus.Incomplete
    }
  });

  if (queryTotal.isLoading || queryIncomplete.isLoading) {
    return <WSLoader.Spinner size="XS" />;
  }

  const total = queryTotal.data?.listSize ?? 0;
  const incomplete = queryIncomplete.data?.listSize ?? 0;
  const complete = total - incomplete;

  return (
    <WSTableCell
      text={
        <WSList gap="XS">
          <WSText.ParagraphSm>{complete} complete</WSText.ParagraphSm>
          <WSText.ParagraphSm>{incomplete} incomplete</WSText.ParagraphSm>
          {isLatestVersion ? (
            <WSText.ParagraphSm>+ new</WSText.ParagraphSm>
          ) : null}
        </WSList>
      }
    />
  );
};

const TABLE_COLUMNS: WSTableColumn<VersionHistoryRow>[] = [
  {
    config: {
      gridTemplateSizeMax: "1fr",
      header: { text: "Versions" }
    },
    renderFunction: ({ data }) => (
      <WSTableCell
        secondaryText={toWSDateString(data.versionCreatedAt, "monthDayYear")}
        secondaryTextColor="gray400"
        text={`v${data.version}`}
      />
    )
  },
  {
    config: {
      gridTemplateSizeMax: "1fr",
      header: { text: "Contractors" }
    },
    renderFunction: ({ data }) => (
      <ContractorsCell
        isLatestVersion={data.isLatestVersion}
        requirementDefinitionId={data.requirementDefinitionId}
        version={data.version}
      />
    )
  }
];

const ModalVersionQuickReference: React.FC<Props> = ({
  onViewDetails,
  requirementDefinition,
  ...modalProps
}) => {
  const versionHistory = requirementDefinition.versionHistory ?? [];
  const latestVersion = requirementDefinition.version;

  const currentVersionEntry: IVersionHistoryItem = {
    minVersion: requirementDefinition.minVersion ?? latestVersion,
    version: latestVersion,
    versionCreatedAt:
      requirementDefinition.versionCreatedAt ?? requirementDefinition.createdAt,
    versionScope: requirementDefinition.versionScope
  };

  const allVersions = [currentVersionEntry, ...versionHistory];

  const tableData = allVersions
    .sort((a, b) => b.version - a.version)
    .map(v => ({
      data: {
        ...v,
        isLatestVersion: v.version === latestVersion,
        requirementDefinitionId: requirementDefinition.requirementDefinitionId
      },
      id: String(v.version)
    }));

  return (
    <WSModal
      actions={{
        alignment: "fill",
        buttons: [
          {
            kind: "Secondary",
            label: "Got it",
            onClick: () => modalProps.onClose()
          },
          ...(onViewDetails
            ? [
                {
                  kind: "Primary" as const,
                  label: "View details",
                  onClick: () => {
                    onViewDetails();
                    modalProps.onClose();
                  }
                }
              ]
            : [])
        ]
      }}
      size="M"
      title="Version quick reference"
      {...modalProps}
    >
      <WSList gap="XL">
        <WSAlert
          icon="info-circle"
          size="M"
          theme="info"
          title="About version quick reference"
        >
          A snapshot of currently active versions. To see full version details,
          go to "View details"
        </WSAlert>

        <WSTable<VersionHistoryRow>
          columns={TABLE_COLUMNS}
          showVerticalDivider
          tableData={tableData}
        />
      </WSList>
    </WSModal>
  );
};

export const useVersionQuickReferenceModal = () =>
  useWSModalCustom(ModalVersionQuickReference);
